import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useModal } from 'react-modal-hook';
import { useHistory, useParams } from 'react-router';
import {
	SpeakerLayoutTypesBold,
	SpeakerLayoutTypesClassic,
	SpeakerLayoutTypesFun,
	TemplateClassNames,
} from '../../../../types/template-layouts';
import { ModuleMap, PageModule, PageModuleType, Speaker } from '../../../../types/working-model';
import { SpeakerDetailsModal } from '../../containers/speaker-details-modal/speaker-details.modal';
import { Button } from '../../../general-ui/button/button';
import { EIcon } from '../../../general-ui/icon/icon';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { ParamsProps } from '../../live-event';
import { getStylingOverrides } from '../../utils';
import SpeakerCard from './speaker-card';
import './speaker-layout.scss';
import { getTextValue } from '../../../../utils/utils';
import { useTranslation } from 'react-i18next';

interface SpeakersProps {
	module: PageModule;
	template: TemplateClassNames;
}

const Speakers = React.forwardRef<HTMLDivElement, SpeakersProps>(({ module, template }, ref) => {
	const [showMore, setShowMore] = useState(false);
	const params: ParamsProps = useParams();
	const language = params?.language || 'en';
	const { t } = useTranslation("homepage");
	const history = useHistory();
	const [speaker, setSpeaker] = useState<Speaker>();
	const { content, styling_overrides, modules } = module;
	const layout_type = content?.layout_type || "standard";

	//init as one variable so it can be passed to the card
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		backgroundColor,
		backgroundImg,
		titleOverrides,
		customHeadingOverrides,
		descriptionOverrides,
	} = stylingOverrides;

	const maxSpeakers = useMemo(() => {
		switch (layout_type) {
			case SpeakerLayoutTypesClassic.standard:
			case SpeakerLayoutTypesBold.standard:
			case SpeakerLayoutTypesFun.standard:
				return 6;

			case SpeakerLayoutTypesClassic.small_horizontal:
			case SpeakerLayoutTypesBold.small_horizontal:
			case SpeakerLayoutTypesFun.small_horizontal:
				return 4;

			case SpeakerLayoutTypesClassic.staggered:
			case SpeakerLayoutTypesBold.staggered:
			case SpeakerLayoutTypesFun.staggered:
				return 2;

			case SpeakerLayoutTypesClassic.long_card:
			case SpeakerLayoutTypesBold.long_card:
			case SpeakerLayoutTypesFun.long_card:
				return 3;

			case SpeakerLayoutTypesClassic.solid:
			case SpeakerLayoutTypesBold.solid:
			case SpeakerLayoutTypesFun.solid:
				return 6;

			case SpeakerLayoutTypesClassic.small_vertical:
			case SpeakerLayoutTypesBold.small_vertical:
			case SpeakerLayoutTypesFun.small_vertical:
				return 4;
			default:
				return 2;
		}
	}, [layout_type]);
	const speakerCount = modules?.length ?? maxSpeakers;
	function limitSpeakersIndex() {
		if (showMore) return speakerCount;
		else return maxSpeakers;
	}
	function handleShowMore() {
		setShowMore((prev) => !prev);
	}

	const [showSpeakerDetailsModal, hideSpeakerDetailsModal] = useModal(
		() => {
			const _relatedSessions = content.related_sessions.find((item: any) => item.speaker === speaker?.speaker);
			return (
				<SpeakerDetailsModal
					template={template}
					onClose={hideSpeakerDetailsModal}
					language={language}
					speaker={speaker as Speaker}
					stylingOverrides={stylingOverrides}
					relatedSessions={_relatedSessions?.sessions ?? []}
					params={params}
					onRelatedSessionClick={history.push}
				/>
			);
		}, [speaker]);

	const { custom_heading, description, title } = getTextValue(content, language);

	return module.is_on && content && (
		<section
			id={ModuleMap[PageModuleType.speakers]}
			className={classNames(
				'speakers',
				`speakers-${layout_type}`,
				template,
				backgroundColor
			)}
			ref={ref}
		>
			<div className="module-container" style={backgroundImg}>
				<TypographyItem className={classNames('evt-caption-1 speakers-custom_heading', customHeadingOverrides)} tagName="p">
					{custom_heading}
				</TypographyItem>
				<TypographyItem className={classNames('evt-heading-2 speakers-title', titleOverrides)} tagName="h2">
					{title}
				</TypographyItem>
				<TypographyItem className="evt-body-text-1 speakers-description">
					<ReactMarkdown
						source={description}
						className={classNames(descriptionOverrides)}
					/>
				</TypographyItem>
				<ul className="speakers-list">
					{modules?.slice(0, limitSpeakersIndex()).map((speaker: Speaker) => (
						<SpeakerCard
							onClick={(speaker) => {
								setSpeaker(speaker);

								showSpeakerDetailsModal();
							}}
							key={speaker.speaker}
							speaker={speaker}
							language={language}
							stylingOverrides={stylingOverrides}
							template={template}
						/>
					))}
				</ul>
				{speakerCount > maxSpeakers && (
					<div className="show-more-wrapper">
						<Button
							classButton={classNames(
								'secondary',
								{ "no-border": template === TemplateClassNames.O2 }
							)}
							template={template}
							onClick={handleShowMore}
						>
							{template == TemplateClassNames.Apollo && (
								<EIcon
									name="refresh-transparent"
									position="order-left"
									size="medium"
								/>
							)}
							{t("Show")} {showMore ? t('Less') : t('More')}
						</Button>
					</div>
				)}
			</div>
		</section>
	);
});

Speakers.displayName = 'Speakers';

export default Speakers;
